import { ClientsEntity, CreateClientsDto, UpdateClientsDto } from '@crm/dto';
import { ApiResponseInterface, ApiResponseStatus, ClientStatus, UserRole } from '@crm/interface';
import { API_RESPONSE, prepareUpdateFields } from '@crm/utils';
import { Injectable, Logger } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { In, Repository } from 'typeorm';

@Injectable()
export class ClientsService {
  private readonly logger = new Logger(ClientsService.name);

  constructor(
    @InjectRepository(ClientsEntity)
    private readonly clientsRepo: Repository<ClientsEntity>,
  ) {}

  // Get all clients
  async getAll(): Promise<ApiResponseInterface> {
    try {
      const clients = await this.clientsRepo.find({
        relations: ['created_by', 'updated_by', 'company'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.CLIENTS.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.CLIENTS.GET_ALL.MESSAGE,
        data: clients,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.CLIENTS.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.CLIENTS.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // Get clients for supervisor: created by managers or supervisors only
  async getForSupervisor(): Promise<ApiResponseInterface> {
    try {
      const clients = await this.clientsRepo.find({
        where: { created_by: { role: In([UserRole.MANAGER, UserRole.SUPERVISOR]) } },
        relations: ['created_by', 'updated_by', 'company'],
      });
      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.CLIENTS.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.CLIENTS.GET_ALL.MESSAGE,
        data: clients,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.CLIENTS.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.CLIENTS.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // Get clients by user ID (creator)
  async getByUserId(userId: string): Promise<ApiResponseInterface> {
    try {
      const clients = await this.clientsRepo.find({
        where: { created_by: { id: userId } },
        relations: ['created_by', 'updated_by', 'company'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.CLIENTS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.CLIENTS.GET_BY_ID.MESSAGE,
        data: clients,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.CLIENTS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.CLIENTS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // Get client by ID
  async getById(id: string): Promise<ApiResponseInterface> {
    try {
      const client = await this.clientsRepo.findOne({
        where: { id },
        relations: ['created_by', 'updated_by', 'company'],
      });

      if (!client) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.CLIENTS.GET_BY_ID.CODE,
          message: 'Client not found',
          data: null,
        };
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.CLIENTS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.CLIENTS.GET_BY_ID.MESSAGE,
        data: client,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.CLIENTS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.CLIENTS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // Create new client
  async create(dto: CreateClientsDto): Promise<ApiResponseInterface> {
    try {
      const newClient = this.clientsRepo.create(dto);
      await this.clientsRepo.save(newClient);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.CLIENTS.CREATE.CODE,
        message: API_RESPONSE.SUCCESS.CLIENTS.CREATE.MESSAGE,
        data: newClient,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.CLIENTS.CREATE.CODE,
        message: API_RESPONSE.ERROR.CLIENTS.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  // Update existing client
  async update(dto: UpdateClientsDto): Promise<ApiResponseInterface> {
    try {
      const client = await this.clientsRepo.findOne({
        where: { id: dto.id },
        relations: ['updated_by', 'company'],
      });

      if (!client) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.CLIENTS.UPDATE.CODE,
          message: 'Client not found',
          data: null,
        };
      }

      const updateData = prepareUpdateFields(dto, {
        updated_by_id: 'updated_by',
        company_id: 'company',
      });

      Object.assign(client, updateData);
      await this.clientsRepo.save(client);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.CLIENTS.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.CLIENTS.UPDATE.MESSAGE,
        data: client,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.CLIENTS.UPDATE.CODE,
        message: API_RESPONSE.ERROR.CLIENTS.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  // Soft delete client (set status to DELETED)
  async softDelete(dto: UpdateClientsDto): Promise<ApiResponseInterface> {
    try {
      const client = await this.clientsRepo.findOne({ where: { id: dto.id } });
      if (!client) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.CLIENTS.DELETE.CODE,
          message: API_RESPONSE.ERROR.CLIENTS.DELETE.MESSAGE,
          data: null,
        };
      }

      client.status = ClientStatus.DELETED;
      await this.clientsRepo.save(client);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.CLIENTS.DELETE.CODE,
        message: API_RESPONSE.SUCCESS.CLIENTS.DELETE.MESSAGE,
        data: client,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.CLIENTS.DELETE.CODE,
        message: API_RESPONSE.ERROR.CLIENTS.DELETE.MESSAGE,
        data: error,
      };
    }
  }
}